import React, { useState, Suspense, lazy } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import DarkModeToggle from './DarkModeToggle';
import Icon from './Icon';
import '../styles/components/navigation.css';
import { useFavorites } from '../contexts/FavoritesContext';
import { PAGES_WITH_OWN_SEARCH, focusPageSearch } from '../utils/pageSearch';

// Lazy-load heavy search components (554 + 571 lines + FontAwesome deps)
const GlobalSearch = lazy(() => import('./GlobalSearch'));
const EnhancedAdvancedSearch = lazy(() => import('./EnhancedAdvancedSearch'));

const Navigation = () => {
  const { getFavoriteModelsCount, getFavoritePhotoshootsCount } = useFavorites();
  const favoritesCount = getFavoriteModelsCount() + getFavoritePhotoshootsCount();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  // Pages with their own search box get a single search: the header button focuses it
  const pageHasOwnSearch = PAGES_WITH_OWN_SEARCH.includes(useLocation().pathname);
  
  const toggleMenu = () => {
      setMenuOpen(!menuOpen);
  };

  return (
    <nav className="main-nav">
      <button 
          className={`mobile-menu-btn ${menuOpen ? 'open' : ''}`} 
          onClick={toggleMenu}
          aria-label="Toggle menu"
      >
          <span></span>
          <span></span>
          <span></span>
      </button>
      
      <div className={`nav-items ${menuOpen ? 'open' : ''}`}>
          <NavLink exact to="/" activeClassName="active" onClick={() => setMenuOpen(false)}>Home</NavLink>
          <NavLink to="/models" activeClassName="active" onClick={() => setMenuOpen(false)}>Models</NavLink>
          <NavLink to="/photoshoots" activeClassName="active" onClick={() => setMenuOpen(false)}>Photoshoots</NavLink>
          <NavLink to="/blog" activeClassName="active" onClick={() => setMenuOpen(false)}>Blog</NavLink>
          <NavLink to="/favorites" activeClassName="active" onClick={() => setMenuOpen(false)}>
            Favorites
            {favoritesCount > 0 && (
              <span className="favorites-count">{favoritesCount}</span>
            )}
          </NavLink>
        
      </div>
      
      <div className={`nav-search ${pageHasOwnSearch ? 'has-page-search' : ''}`}>
        {!pageHasOwnSearch && (
          <Suspense fallback={<div className="desktop-search" />}>
            <GlobalSearch className="desktop-search" />
          </Suspense>
        )}
        <button 
          className="mobile-search-btn"
          onClick={pageHasOwnSearch ? focusPageSearch : () => setShowAdvancedSearch(true)}
          aria-label={pageHasOwnSearch ? 'Search this page' : 'Open search'}
          title={pageHasOwnSearch ? 'Search this page (Ctrl+K)' : undefined}
        >
          <Icon name="search" />
        </button>
      </div>
      
      <div className="nav-actions">
          <button 
            className="advanced-search-btn"
            onClick={() => setShowAdvancedSearch(true)}
            title="Advanced model search (Ctrl+Shift+F)"
            aria-label="Advanced model search"
          >
            <Icon name="sliders" />
          </button>
          <DarkModeToggle />
      </div>

      {showAdvancedSearch && (
        <Suspense fallback={null}>
          <EnhancedAdvancedSearch 
            isOpen={showAdvancedSearch}
            onClose={() => setShowAdvancedSearch(false)}
          />
        </Suspense>
      )}
    </nav>
  );
};

export default Navigation;